import { useEffect, useRef, useState } from 'react';
import type { PropsWithChildren, ReactPortal } from 'react';
import { createPortal } from 'react-dom';
import BodyScrollPreventer from 'common/components/BodyScrollPreventer';

import styles from './ModalBackground.module.scss';
import { ModalDialog } from './ModalDialog';
import ModalTransition from './ModalTransition';

type ModalBackgroundProps = PropsWithChildren<{
  onClose: () => void;
}>;

export function ModalBackground({
  onClose,
  children
}: ModalBackgroundProps): ReactPortal | null {
  const [renderModal, setRenderModal] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  if (!nodeRef.current) {
    nodeRef.current = document.createElement('div');
  }

  useEffect(() => {
    const node = nodeRef.current;
    if (node && document.body) {
      document.body.appendChild(node);
    }
    return () => {
      if (node && document.body) {
        document.body.removeChild(node);
      }
    };
  }, []);

  useEffect(() => {
    // Work around Mobile Safari scrolling the body to the top of the page when modal mounts.
    // By delaying the modal rendering by two ticks, we allow Safari to recover the viewport
    // height after a form field has been blurred, and to not screw up the scroll position
    // when a backdrop with `position: fixed` style is added to the DOM.
    //
    // Two ticks are needed, as hiding keyboard seems to take one tick and hiding the bottom
    // takes another tick.
    //
    // See https://github.com/venuu/venuu/pull/2293#issuecomment-270116195
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setRenderModal(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  if (!renderModal) {
    return null;
  }

  return createPortal(
    <ModalTransition>
      <BodyScrollPreventer
        getScrollLimitElement={() => scrollContainerRef.current}
      >
        <ModalDialog
          onClose={onClose}
          backdropClassName={styles.fullPageBackground}
          modalClassName={styles.modalScrollContainer}
          scrollContainerRef={scrollContainerRef}
        >
          <div>
            <div
              className={styles.fakeFullHeightBackground}
              onClick={onClose}
              data-testid="modal-backdrop"
            />
            {children}
          </div>
        </ModalDialog>
      </BodyScrollPreventer>
    </ModalTransition>,
    nodeRef.current
  );
}
